import {
  CompanyColumnField,
  ALL_COMPANY_COLUMN_FIELDS,
  getDefaultCompanyColumns,
  COMPANY_COLUMN_PREFERENCES_KEY,
} from '../components/table/company-column-groups';

export interface CompanyColumnPreferences {
  visibleColumns: string[];
  columnOrder: string[];
}

/**
 * Get saved company column preferences from localStorage
 */
export const getCompanyColumnPreferences = (): CompanyColumnPreferences | null => {
  try {
    const savedPrefs = localStorage.getItem(COMPANY_COLUMN_PREFERENCES_KEY);
    if (savedPrefs) {
      return JSON.parse(savedPrefs);
    }
  } catch (error) {
    // ignore localStorage read failures
  }
  return null;
};

/**
 * Save company column preferences to localStorage
 */
export const saveCompanyColumnPreferences = (
  prefs: CompanyColumnPreferences,
): void => {
  try {
    localStorage.setItem(COMPANY_COLUMN_PREFERENCES_KEY, JSON.stringify(prefs));
  } catch (error) {
    // ignore localStorage write failures
  }
};

/**
 * Get initial visible columns - from saved prefs or defaults
 */
export const getInitialVisibleCompanyColumns = (): string[] => {
  const savedPrefs = getCompanyColumnPreferences();
  if (savedPrefs?.visibleColumns?.length) {
    return savedPrefs.visibleColumns;
  }
  return getDefaultCompanyColumns().map((col) => String(col.id));
};

/**
 * Get initial column order - from saved prefs or default order
 */
export const getInitialCompanyColumnOrder = (): string[] => {
  const savedPrefs = getCompanyColumnPreferences();
  if (savedPrefs?.columnOrder?.length) {
    return savedPrefs.columnOrder;
  }
  return ALL_COMPANY_COLUMN_FIELDS.map((col) => String(col.id));
};

/**
 * Reset company column preferences to defaults
 */
export const resetCompanyColumnPreferences = (): void => {
  try {
    localStorage.removeItem(COMPANY_COLUMN_PREFERENCES_KEY);
  } catch (error) {
    // ignore localStorage remove failures
  }
};

/**
 * Get visible company column fields based on current preferences
 */
export const getVisibleCompanyColumnFields = (
  visibleColumns: string[],
): CompanyColumnField[] =>
  ALL_COMPANY_COLUMN_FIELDS.filter((field) =>
    visibleColumns.includes(String(field.id)),
  );

/**
 * Check if a column can be hidden (non-removable columns cannot be hidden)
 */
export const canHideCompanyColumn = (columnId: string): boolean => {
  const column = ALL_COMPANY_COLUMN_FIELDS.find(
    (col) => String(col.id) === columnId,
  );
  return column ? !column.isNonRemovable : false;
};
